import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { RegisterAdminForm } from "shared/types";
import { invalidateEmployeeLists } from "hooks/mutations/employeeLists";
import { translate } from "shared/languages";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";

/**
 * Create a studio admin, or give an existing client / instructor dashboard
 * access. One endpoint, two body shapes (see RegisterAdminForm).
 *
 * `linked` only changes the toast: both paths end with an account that has to
 * run the Firebase OTP step on its first dashboard sign-in, which is the part
 * worth telling the System Admin.
 */
export const useRegisterAdmin = (linked = false) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (registerForm: RegisterAdminForm) => {
      return client.post("/v1/admin", registerForm);
    },
    onSuccess: () => {
      getApiSuccessMessage(
        translate(
          linked ? "messages.success.admin_linked" : "messages.success.admin_created",
        ),
      );
      // The prefix, not ["business", id]: the response is a user row and has no
      // business_id, so the old key was ["business", undefined] and matched
      // nothing — the Akun table never refreshed after registering an admin.
      queryClient.invalidateQueries({ queryKey: ["business"] });
      // An existing instructor may have just been promoted, so the lists that
      // show them change too.
      invalidateEmployeeLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
